import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getSellerTerminalsQuery, getTerminslsQuery } from "../../queries";
import TableContainer from "../Common/TableContainer";
import {
  bindTerminalToSeller,
  removeTerminalFromSeller,
} from "../../api/seller";
import DeleteModal from "../Common/DeleteModal";
import {
  Row,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";

export function SellerTerminals() {
  const [modal, setModal] = useState(false);
  const { sellerId } = useParams();
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: terminals, isError, error } = useQuery({
    ...getSellerTerminalsQuery(sellerId),
  });

  const { data: availableTerminalsData } = useQuery({
    ...getTerminslsQuery({
      page: 1,
      size: 100,
      hasSeller: false,
    }),
  });

  const availableTerminals = availableTerminalsData?.data?.data || [];

  useEffect(() => {
    if (isError) {
      alert(error?.response?.data?.error?.message || error?.message);
    }
  }, [isError, error]);

  const toggle = () => {
    setModal(!modal);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      commissionPercent: "",
      terminalId: "",
    },
    validationSchema: Yup.object({
      commissionPercent: Yup.string().required(t("Please enter commission")),
      terminalId: Yup.string().required(t("Please enter terminal number")),
    }),
    onSubmit: (values, { resetForm }) => {
      bindTerminalToSeller(sellerId, values)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["seller-terminals", sellerId],
          });
          queryClient.invalidateQueries({
            queryKey: [
              "terminals",
              {
                page: 1,
                size: 100,
                hasSeller: false,
              },
            ],
          });
          resetForm();
          toggle();
        })
        .catch((err) => {
          alert(err?.response?.data?.error?.message || err?.message);
        });
    },
  });

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "terminal.name",
        enableColumnFilter: false,
      },
      {
        header: t("Type"),
        accessorKey: "terminal.type",
        enableColumnFilter: false,
      },
      {
        header: t("Serial number"),
        accessorKey: "terminal.posHardwareSerialNumber",
        enableColumnFilter: false,
      },
      {
        header: t("Comment"),
        accessorKey: "terminal.comment",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.terminal.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    [t]
  );

  const onDelete = () => {
    removeTerminalFromSeller(sellerId, deleteModal)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["seller-terminals", sellerId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            "terminals",
            {
              page: 1,
              size: 100,
              hasSeller: false,
            },
          ],
        });
      })
      .catch((err) => {
        alert(err?.response?.data?.error?.message || err?.message);
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  return (
    <>
      <div className="card-body pt-0" style={{ padding: 0 }}>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={onDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <div>
          <Row className="g-4 mb-4 align-items-center">
            <div className="col-sm">
              <div>
                <h5 className="card-title mb-0">{t("Terminals")}</h5>
              </div>
            </div>
            <div className="col-sm-auto">
              <div>
                <button
                  type="button"
                  className="btn btn-success add-btn"
                  id="create-btn"
                  onClick={() => setModal(true)}
                >
                  <i className="ri-add-line align-bottom me-1"></i>{" "}
                  {t("Terminal biriktirish")}
                </button>
              </div>
            </div>
          </Row>
          {terminals?.data?.data?.length > 0 ? (
            <TableContainer
              columns={columns}
              data={terminals?.data?.data || []}
              isAddUserList={false}
              customPageSize={10}
              className="custom-header-css"
              SearchPlaceholder="Search for user, email, phone, status or something..."
            />
          ) : (
            <div className="text-center p-3 text-muted">
              <h5>{t("No Data")}</h5>
            </div>
          )}
        </div>
        <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
          <ModalHeader className="bg-light p-3" toggle={toggle}>
            {t("Assign Terminal")}
          </ModalHeader>
          <Form
            className="tablelist-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label htmlFor="terminalId-field" className="form-label">
                  {t("Terminal")}
                </Label>
                <Input
                  name="terminalId"
                  id="terminalId-field"
                  className="form-control"
                  type="select"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.terminalId || ""}
                  invalid={
                    validation.touched.terminalId && validation.errors.terminalId
                      ? true
                      : false
                  }
                >
                  <option value="">{t("Select Terminal")}</option>
                  {availableTerminals?.map((terminal) => (
                    <option key={terminal.id} value={terminal.id}>
                      {terminal.name} ({terminal.posHardwareSerialNumber})
                    </option>
                  ))}
                </Input>
                {validation.touched.terminalId && validation.errors.terminalId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.terminalId}
                  </FormFeedback>
                ) : null}
              </div>

              <div className="mb-3">
                <Label htmlFor="commissionPercent-field" className="form-label">
                  {t("Commission Percent")}
                </Label>
                <Input
                  name="commissionPercent"
                  id="commissionPercent-field"
                  className="form-control"
                  placeholder={t("Enter Commission Percent")}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.commissionPercent ?? ""}
                  invalid={
                    validation.touched.commissionPercent &&
                    validation.errors.commissionPercent
                      ? true
                      : false
                  }
                />
                {validation.touched.commissionPercent &&
                validation.errors.commissionPercent ? (
                  <FormFeedback type="invalid">
                    {validation.errors.commissionPercent}
                  </FormFeedback>
                ) : null}
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="hstack gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setModal(false);
                  }}
                >
                  {t("Close")}
                </button>

                <button type="submit" className="btn btn-success">
                  {t("Assign Terminal")}
                </button>
              </div>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </>
  );
}
